import type { z } from 'zod';
import kebabCase from 'lodash/kebabCase.js';
import trim from 'lodash/trim.js';
import truncate from 'lodash/truncate.js';
import { isReservedCustomElementName } from '../../../dev-center/components/validations/custom-element.js';

const MAX_DERIVED_FOLDER_LENGTH = 50;

const VALID_CUSTOM_ELEMENT_NAME_REGEX = /^[a-z][a-z0-9]*(-[a-z0-9]+)+$/;

function deriveFolder(source: string): string | undefined {
  const folder = truncate(
    trim(kebabCase(source).replaceAll(/[^a-z0-9]+/g, '-'), '-').replace(
      /^[^a-z]+/,
      ''
    ),
    { length: MAX_DERIVED_FOLDER_LENGTH, separator: '-', omission: '' }
  );

  if (!folder) {
    return undefined;
  }

  return folder;
}

function deriveCustomElementFolder(
  source: string,
  suffix: string
): string | undefined {
  const folder = deriveFolder(source);

  if (!folder) {
    return undefined;
  }

  const elementFolder =
    folder.includes('-') && !isReservedCustomElementName(folder)
      ? folder
      : `${folder}-${suffix}`;

  return VALID_CUSTOM_ELEMENT_NAME_REGEX.test(elementFolder)
    ? elementFolder
    : undefined;
}

interface DerivedFolderOptions<T extends string> {
  sourceField: T;
}

export type WithDerivedFolder<Schema extends z.ZodTypeAny> =
  z.output<Schema> & { folder: string };

export function enrichWithDerivedFolder<
  T extends Record<DerivedFolderOptions<K>['sourceField'], string> & {
    folder?: string;
  },
  K extends string,
>(data: T, { sourceField }: DerivedFolderOptions<K>): T & { folder: string } {
  const source = data[sourceField];
  const folder = data.folder ?? deriveFolder(source);

  if (!folder) {
    throw new Error(
      `Could not derive a folder from ${sourceField} '${source}'. Use a ${sourceField} containing latin letters, or pass 'folder' explicitly.`
    );
  }

  return { ...data, folder };
}

export function enrichCustomElementWithDerivedFolder<
  T extends Record<DerivedFolderOptions<K>['sourceField'], string> & {
    folder?: string;
  },
  K extends string,
>(
  data: T,
  {
    sourceField,
    suffix,
  }: DerivedFolderOptions<K> & {
    suffix: string;
  }
): T & { folder: string } {
  const source = data[sourceField];
  const folder = data.folder ?? deriveCustomElementFolder(source, suffix);

  if (!folder) {
    throw new Error(
      `Could not derive a folder from ${sourceField} '${source}'. Use a ${sourceField} containing latin letters, or pass 'folder' explicitly.`
    );
  }

  return { ...data, folder };
}
